import type { AppLocale } from "./locale";
import { legacyMessages } from "./legacyMessages";

export type LocaleMessages = Record<string, string>;
export type LocaleCatalog = Record<AppLocale, LocaleMessages>;

export const defaultMessages: LocaleCatalog = {
  en: {
    ...legacyMessages.en,
    "profile.title": "Profile",
    "profile.subtitle": "Manage your personal info and login identity",
    "profile.email": "Email",
    "profile.fullName": "Full Name",
    "profile.picture": "Profile picture",
    "profile.upload": "Upload",
    "profile.languageLocale.title": "Language & Locale",
    "profile.languageLocale.subtitle": "Set interface language and time zone",
    "profile.language": "Default UI language",
    "profile.timeZone": "Time zone",
    "profile.appearance.title": "Appearance",
    "profile.appearance.subtitle": "Customize the appearance of the app",
    "profile.theme": "Theme",
    "profile.theme.light": "Light",
    "profile.theme.dark": "Dark",
    "profile.badge.soon": "soon",
    "profile.saveChanges": "Save changes",
    "profile.saved": "The changes were saved!",
    "locale.english": "English",
    "locale.ukrainian": "Ukrainian",
    "common.soon": "soon",
    "common.comingSoon": "coming soon",
    "common.loading": "Loading...",
    "common.user": "User",
    "common.prompt": "Prompt",
    "common.position": "Position",
    "common.visibility": "Visibility",
    "common.country": "Country",
    "common.response": "Response",
    "common.platforms": "Platforms",
    "common.brand": "Brand",
    "common.domain": "Domain",
    "common.used": "Used",
    "common.prompts": "prompts",
    "common.search": "Search...",
    "common.allTags": "All tags",
    "common.allCountries": "All countries",
    "common.allPlatforms": "All platforms",
    "common.noResponse": "No Response",
    "common.notTriggered": "Not Triggered",
    "common.status": "Status",
    "common.allPromptsFiltered": "All prompts are filtered",
    "common.noPromptsYet": "No prompts yet",
    "common.clearSearchToSeePrompts":
      "Please clear the search to see all prompts.",
    "common.noPromptsCreated":
      "You haven't created any prompts yet. Get started by creating your first prompt.",
    "common.noPromptsGenerated":
      "You haven't generated any prompts yet. Get started by generating your prompts first.",
    "common.showOriginal": "Show original",
    "common.showTranslated": "Show translated",
    "prompts.action.getSuggested": "Get Suggested Prompts",
    "prompts.action.boost": "Boost Prompts",
    "prompts.action.retryBoost": "Retry Boost",
    "prompts.action.processing": "Processing",
    "prompts.boost.processing":
      "Boost is generating actions. This can take a few minutes.",
    "prompts.boost.trialLimit":
      "Trial boost limit reached for this cluster. Upgrade to unlock additional clusters.",
    "shell.group.track": "Track",
    "shell.group.act": "Act",
    "shell.group.workspace": "Workspace",
    "shell.nav.dashboard": "Dashboard",
    "shell.nav.prompts": "Prompts",
    "shell.nav.competitors": "Competitors",
    "shell.nav.sources": "Sources",
    "shell.nav.foundation": "Foundation",
    "shell.nav.clusterActions": "Cluster Actions",
    "shell.nav.strategy": "Strategy",
    "shell.nav.tasks": "Tasks",
    "shell.nav.plansPricing": "Plans & Pricing",
    "shell.nav.settings": "Settings",
    "shell.nav.support": "Support",
    "settings.nav.company": "Company",
    "settings.nav.brand": "Brand",
    "settings.nav.account": "Account",
    "settings.nav.notifications": "Notifications",
    "settings.nav.billing": "Billing",
    "settings.nav.integrations": "Integrations",
    "settings.integrations.title": "Integrations",
    "settings.integrations.subtitle":
      "Connect external data sources to enrich visibility insights and AI recommendations",
    "settings.integrations.filterLabel": "Integration status",
    "settings.integrations.filter.all": "All",
    "settings.integrations.filter.connected": "Connected",
    "settings.integrations.filter.available": "Available",
    "settings.integrations.searchPlaceholder": "Search integrations...",
    "settings.integrations.oauth.connectFailed":
      "Could not start Google connection.",
    "settings.integrations.oauth.continueToGoogle": "Continue to Google",
    "settings.integrations.oauth.grantTitle": "Grant access to Google",
    "settings.integrations.oauth.grantDescription":
      "Complete the sign-in steps in the new browser tab.",
    "settings.integrations.oauth.didntWork": "Didn't work?",
    "settings.integrations.oauth.reopenTab": "Reopen the tab",
    "settings.integrations.oauth.completedSteps": "I've completed the steps",
    "settings.integrations.oauth.readOnlyNote": "Read-only where possible",
    "settings.integrations.oauth.disconnectAnytime": "Disconnect anytime",
    "settings.integrations.oauth.providerHint":
      "After Google redirects back, return here and choose the {providerName} property.",
    "settings.integrations.disconnect.title": "Disconnect {integrationName}?",
    "settings.integrations.disconnect.description":
      "Nobori will remove the stored Google token and synced metric snapshots for this brand. You can connect it again later.",
    "settings.integrations.badge.soon": "Soon",
    "settings.integrations.ga4.name": "Google Analytics 4",
    "settings.integrations.ga4.description":
      "Measure sessions, conversions, and landing page performance influenced by AI visibility",
    "settings.integrations.ga4.connectTitle": "Connect Google Analytics 4",
    "settings.integrations.ga4.connectDescription":
      "Authorize Google access to connect analytics data to this workspace.",
    "settings.integrations.ga4.connected":
      "Google Analytics 4 is connected and syncing.",
    "settings.integrations.ga4.disconnected":
      "Google Analytics 4 has been disconnected.",
    "settings.integrations.ga4.connectFailed":
      "Could not connect Google Analytics 4.",
    "settings.integrations.ga4.disconnectFailed":
      "Could not disconnect Google Analytics 4.",
    "settings.integrations.ga4.selectFailed":
      "Could not save the selected Google Analytics property.",
    "settings.integrations.ga4.selectTitle":
      "Choose a Google Analytics property",
    "settings.integrations.ga4.selectDescription":
      "Pick the GA4 property Nobori should sync for this brand.",
    "settings.integrations.ga4.accountLabel": "Account",
    "settings.integrations.ga4.accountPlaceholder": "Choose account",
    "settings.integrations.ga4.defaultAccount": "Google Analytics account",
    "settings.integrations.ga4.propertyLabel": "Property",
    "settings.integrations.ga4.selectionHint":
      "You can change it later in settings.",
    "settings.integrations.ga4.connectProperty": "Connect property",
    "settings.integrations.ga4.loadingProperties": "Loading properties...",
    "settings.integrations.ga4.noProperties":
      "No GA4 properties were found. Use a Google account with access to a GA4 property for this brand.",
    "settings.integrations.gsc.name": "Google Search Console",
    "settings.integrations.gsc.description":
      "Track organic queries, impressions, clicks, and visibility trends alongside AI discovery",
    "settings.integrations.gsc.connectTitle": "Connect Google Search Console",
    "settings.integrations.gsc.connectDescription":
      "Authorize Google access to connect Search Console data to this workspace.",
    "settings.integrations.webflow.description":
      "Publish optimized pages and AI-ready content directly to your Webflow CMS",
    "settings.integrations.notion.description":
      "Use Notion pages and databases as structured content sources for AI workflows",
    "settings.integrations.github.description":
      "Connect repositories and documentation as searchable context for AI systems",
    "settings.integrations.action.connect": "Connect",
    "settings.integrations.action.manage": "Manage",
    "settings.integrations.action.reconnect": "Reconnect",
    "settings.integrations.action.disconnect": "Disconnect",
    "settings.integrations.action.cancel": "Cancel",
    "settings.integrations.tooltip.connect": "Connect integration",
    "settings.integrations.tooltip.connectSoon":
      "Connection flow is coming soon",
    "settings.integrations.tooltip.manage": "Manage integration",
    "settings.integrations.tooltip.reconnect": "Reconnect Google account",
    "settings.integrations.tooltip.disconnect": "Disconnect integration",
    "settings.integrations.tooltip.soon": "This integration is coming soon",
    "settings.integrations.status.connected": "Connected",
    "settings.integrations.status.actionRequired":
      "Action required. Choose a property or reconnect this source.",
    "settings.integrations.property": "Property: {propertyName}",
    "settings.integrations.gsc.connected":
      "Google Search Console is connected and syncing.",
    "settings.integrations.gsc.disconnected":
      "Google Search Console has been disconnected.",
    "settings.integrations.gsc.connectFailed":
      "Could not connect Google Search Console.",
    "settings.integrations.gsc.disconnectFailed":
      "Could not disconnect Google Search Console.",
    "settings.integrations.gsc.selectFailed":
      "Could not save the selected Search Console site.",
    "settings.integrations.gsc.selectTitle": "Choose a Search Console site",
    "settings.integrations.gsc.selectDescription":
      "Pick the verified site property Nobori should sync for this brand.",
    "settings.integrations.gsc.connectSite": "Connect site",
    "settings.integrations.gsc.loadingSites": "Loading sites...",
    "settings.integrations.gsc.noSites":
      "No Search Console sites were found for this Google account.",
    "settings.integrations.gsc.unverifiedSite":
      "Not verified in Search Console",
    "settings.integrations.empty.title": "No integrations found",
    "settings.integrations.empty.subtitle":
      "Try a different search or status filter.",
    "date.today": "Today",
    "date.yesterday": "Yesterday",
    "date.last7Days": "Last 7 days",
    "date.last14Days": "Last 14 days",
    "date.last30Days": "Last 30 days",
    "date.custom": "Custom",
    "dashboard.competitors.loadingTitle": "Finding your top competitors...",
    "dashboard.competitors.loadingSubtitle":
      "This can take a few minutes depending on how many prompts are processed",
    "dashboard.competitors.bannerTitle": "Add more competitors",
    "dashboard.competitors.bannerBody":
      "We auto-added competitors. Review or add more.",
    "dashboard.competitors.bannerButton": "Add Competitor",
    "dataChat.hero.title": "Tell me what to work on",
    "dataChat.hero.subtitle":
      "Analyze visibility, competitors, prompts, and cited sources",
    "dataChat.hero.startWith": "or start with one of these",
    "dataChat.starter.visibilityGaps": "Analyze visibility gaps",
    "dataChat.starter.citationGaps": "Find citation gaps",
    "dataChat.starter.competitors": "Analyze competitors",
    "dataChat.starter.recentChanges": "Summarize recent changes",
    "dataChat.placeholder.ask":
      "Ask about visibility gaps, competitors, prompts, or cited domains...",
    "dataChat.placeholder.summarize":
      "Summarize {brandName}'s visibility changes from the last {days} days...",
    "dataChat.placeholder.citationGaps":
      "Find citation gaps competitors are winning...",
    "dataChat.placeholder.compareCompetitors":
      "Compare my brand against the strongest competitor...",
    "dataChat.loading.gathering": "Gathering Nobori data",
    "dataChat.loading.savingQuestion": "Saving your question",
    "dataChat.loading.planning": "Planning the analysis",
    "dataChat.loading.queryingSources": "Querying {sources}",
    "dataChat.loading.sourcesCompleted":
      "Completed {completed} of {total} data reads",
    "dataChat.loading.checkingClusters": "Checking prompt clusters",
    "dataChat.loading.reviewingCitations": "Reviewing citation data",
    "dataChat.loading.comparingCompetitors": "Comparing competitor visibility",
    "dataChat.loading.checkingTasks": "Checking tasks and priorities",
    "dataChat.loading.reviewingPrompts": "Reviewing prompt results",
    "dataChat.loading.reviewingVisibility": "Reviewing visibility data",
    "dataChat.loading.reviewingRelevant": "Reviewing relevant Nobori data",
    "dataChat.loading.preparing": "Preparing your answer",
    "dataChat.loading.chat": "Loading chat...",
    "dataChat.action.copyResponse": "Copy response",
    "dataChat.action.copiedResponse": "Copied response",
    "dataChat.action.tryAgain": "Try again",
    "dataChat.action.cancel": "Cancel",
    "dataChat.action.send": "Send",
    "dataChat.action.share": "Share",
    "dataChat.action.delete": "Delete",
    "dataChat.action.copyMessage": "Copy message",
    "dataChat.action.copiedMessage": "Copied message",
    "dataChat.action.editMessage": "Edit message",
    "dataChat.soon": "Coming soon",
    "dataChat.shareSoon": "Share coming soon",
    "dataChat.attachSoon": "Attach file coming soon",
    "dataChat.thread.new": "New Chat",
    "dataChat.shell.learn": "Learn",
    "dataChat.sidebar.browse": "Browse",
    "dataChat.sidebar.chat": "Chat",
    "dataChat.sidebar.newBadge": "new",
    "dataChat.sidebar.tasks": "Tasks",
    "dataChat.sidebar.searchChats": "Search chats...",
    "dataChat.sidebar.pinned": "Pinned",
    "dataChat.sidebar.today": "Today",
    "dataChat.sidebar.last7Days": "Last 7 Days",
    "dataChat.sidebar.last30Days": "Last 30 Days",
    "dataChat.sidebar.older": "Older",
    "dataChat.sidebar.loadingChats": "Loading chats...",
    "dataChat.sidebar.noChats": "No saved chats yet.",
    "dataChat.sidebar.chatOptions": "Chat options",
    "dataChat.sidebar.rename": "Rename",
    "dataChat.sidebar.pin": "Pin to Chat",
    "dataChat.sidebar.unpin": "Unpin chat",
    "dataChat.sidebar.deleteTitle": "Delete chat?",
    "dataChat.sidebar.deleteDescription": "This will delete",
    "dataChat.sidebar.thisChat": "this chat",
    "dataChat.toast.loadFailed": "Couldn't load this chat",
    "dataChat.toast.answerFailed": "Couldn't answer from your data",
    "dataChat.toast.copyAnswerFailed": "Couldn't copy answer",
    "dataChat.toast.copyMessageFailed": "Couldn't copy message",
    "dataChat.toast.reloadBeforeRetry":
      "Reload this chat before trying that answer again",
    "dataChat.error.selectBrand": "Select a brand first",
  },
  uk: {
    ...legacyMessages.uk,
    "profile.title": "Профіль",
    "profile.subtitle": "Керуйте особистими даними та обліковим записом",
    "profile.email": "Електронна пошта",
    "profile.fullName": "Повне ім'я",
    "profile.picture": "Фото профілю",
    "profile.upload": "Завантажити",
    "profile.languageLocale.title": "Мова та локаль",
    "profile.languageLocale.subtitle":
      "Налаштуйте мову інтерфейсу й часовий пояс",
    "profile.language": "Мова інтерфейсу",
    "profile.timeZone": "Часовий пояс",
    "profile.appearance.title": "Вигляд",
    "profile.appearance.subtitle": "Налаштуйте вигляд застосунку",
    "profile.theme": "Тема",
    "profile.theme.light": "Світла",
    "profile.theme.dark": "Темна",
    "profile.badge.soon": "скоро",
    "profile.saveChanges": "Зберегти зміни",
    "profile.saved": "Зміни збережено!",
    "locale.english": "Англійська",
    "locale.ukrainian": "Українська",
    "common.soon": "скоро",
    "common.comingSoon": "скоро",
    "common.loading": "Завантаження...",
    "common.user": "Користувач",
    "common.prompt": "Промпт",
    "common.position": "Позиція",
    "common.visibility": "Видимість",
    "common.country": "Країна",
    "common.response": "Відповідь",
    "common.platforms": "Платформи",
    "common.brand": "Бренд",
    "common.domain": "Домен",
    "common.used": "Використано",
    "common.prompts": "промптів",
    "common.search": "Пошук...",
    "common.allTags": "Усі теги",
    "common.allCountries": "Усі країни",
    "common.allPlatforms": "Усі платформи",
    "common.noResponse": "Немає відповіді",
    "common.notTriggered": "Не запускалось",
    "common.status": "Статус",
    "common.allPromptsFiltered": "Усі промпти відфільтровано",
    "common.noPromptsYet": "Промптів ще немає",
    "common.clearSearchToSeePrompts":
      "Очистьте пошук, щоб побачити всі промпти.",
    "common.noPromptsCreated":
      "Ви ще не створили жодного промпта. Почніть зі створення першого промпта.",
    "common.noPromptsGenerated":
      "Ви ще не згенерували жодного промпта. Почніть із генерації промптів.",
    "common.showOriginal": "Показати оригінал",
    "common.showTranslated": "Показати переклад",
    "prompts.action.getSuggested": "Отримати запропоновані промпти",
    "prompts.action.boost": "Підсилити промпти",
    "prompts.action.retryBoost": "Повторити підсилення",
    "prompts.action.processing": "Обробка",
    "prompts.boost.processing":
      "Підсилення генерує дії. Це може зайняти кілька хвилин.",
    "prompts.boost.trialLimit":
      "Ліміт trial-підсилення для цього кластера вичерпано. Оновіть план, щоб розблокувати додаткові кластери.",
    "shell.group.track": "Відстеження",
    "shell.group.act": "Дії",
    "shell.group.workspace": "Робочий простір",
    "shell.nav.dashboard": "Дашборд",
    "shell.nav.prompts": "Промпти",
    "shell.nav.competitors": "Конкуренти",
    "shell.nav.sources": "Джерела",
    "shell.nav.foundation": "Фундамент",
    "shell.nav.clusterActions": "Дії кластерів",
    "shell.nav.strategy": "Стратегія",
    "shell.nav.tasks": "Задачі",
    "shell.nav.plansPricing": "Плани та ціни",
    "shell.nav.settings": "Налаштування",
    "shell.nav.support": "Підтримка",
    "settings.nav.company": "Компанія",
    "settings.nav.brand": "Бренд",
    "settings.nav.account": "Обліковий запис",
    "settings.nav.notifications": "Сповіщення",
    "settings.nav.billing": "Білінг",
    "settings.nav.integrations": "Інтеграції",
    "settings.integrations.title": "Інтеграції",
    "settings.integrations.subtitle":
      "Підключайте зовнішні джерела даних, щоб покращити інсайти видимості та AI-рекомендації",
    "settings.integrations.filterLabel": "Статус інтеграції",
    "settings.integrations.filter.all": "Усі",
    "settings.integrations.filter.connected": "Підключені",
    "settings.integrations.filter.available": "Доступні",
    "settings.integrations.searchPlaceholder": "Пошук інтеграцій...",
    "settings.integrations.oauth.connectFailed":
      "Не вдалося почати підключення Google.",
    "settings.integrations.oauth.continueToGoogle": "Продовжити в Google",
    "settings.integrations.oauth.grantTitle": "Надайте доступ Google",
    "settings.integrations.oauth.grantDescription":
      "Завершіть кроки входу в новій вкладці браузера.",
    "settings.integrations.oauth.didntWork": "Не спрацювало?",
    "settings.integrations.oauth.reopenTab": "Відкрити вкладку знову",
    "settings.integrations.oauth.completedSteps": "Я завершив кроки",
    "settings.integrations.oauth.readOnlyNote": "Тільки читання, де це можливо",
    "settings.integrations.oauth.disconnectAnytime":
      "Можна відключити будь-коли",
    "settings.integrations.oauth.providerHint":
      "Після повернення з Google поверніться сюди й оберіть ресурс {providerName}.",
    "settings.integrations.disconnect.title": "Відключити {integrationName}?",
    "settings.integrations.disconnect.description":
      "Nobori видалить збережений Google-токен і синхронізовані знімки метрик для цього бренду. Ви зможете підключити інтеграцію знову пізніше.",
    "settings.integrations.badge.soon": "Скоро",
    "settings.integrations.ga4.name": "Google Analytics 4",
    "settings.integrations.ga4.description":
      "Вимірюйте сесії, конверсії та ефективність посадкових сторінок, на які впливає AI-видимість",
    "settings.integrations.ga4.connectTitle": "Підключіть Google Analytics 4",
    "settings.integrations.ga4.connectDescription":
      "Авторизуйте доступ Google, щоб підключити аналітичні дані до цього робочого простору.",
    "settings.integrations.ga4.connected":
      "Google Analytics 4 підключено, синхронізацію запущено.",
    "settings.integrations.ga4.disconnected": "Google Analytics 4 відключено.",
    "settings.integrations.ga4.connectFailed":
      "Не вдалося підключити Google Analytics 4.",
    "settings.integrations.ga4.disconnectFailed":
      "Не вдалося відключити Google Analytics 4.",
    "settings.integrations.ga4.selectFailed":
      "Не вдалося зберегти вибраний ресурс Google Analytics.",
    "settings.integrations.ga4.selectTitle": "Оберіть ресурс Google Analytics",
    "settings.integrations.ga4.selectDescription":
      "Виберіть GA4-ресурс, який Nobori має синхронізувати для цього бренду.",
    "settings.integrations.ga4.accountLabel": "Акаунт",
    "settings.integrations.ga4.accountPlaceholder": "Оберіть акаунт",
    "settings.integrations.ga4.defaultAccount": "Акаунт Google Analytics",
    "settings.integrations.ga4.propertyLabel": "Ресурс",
    "settings.integrations.ga4.selectionHint":
      "Це можна змінити пізніше в налаштуваннях.",
    "settings.integrations.ga4.connectProperty": "Підключити ресурс",
    "settings.integrations.ga4.loadingProperties": "Завантажуємо ресурси...",
    "settings.integrations.ga4.noProperties":
      "GA4-ресурсів не знайдено. Використайте Google-акаунт із доступом до GA4-ресурсу цього бренду.",
    "settings.integrations.gsc.name": "Google Search Console",
    "settings.integrations.gsc.description":
      "Відстежуйте органічні запити, покази, кліки та тренди видимості поруч з AI-виявленням",
    "settings.integrations.gsc.connectTitle":
      "Підключіть Google Search Console",
    "settings.integrations.gsc.connectDescription":
      "Авторизуйте доступ Google, щоб підключити дані Search Console до цього робочого простору.",
    "settings.integrations.webflow.description":
      "Публікуйте оптимізовані сторінки та AI-ready контент напряму у Webflow CMS",
    "settings.integrations.notion.description":
      "Використовуйте сторінки та бази Notion як структуровані джерела контенту для AI-процесів",
    "settings.integrations.github.description":
      "Підключайте репозиторії та документацію як пошуковий контекст для AI-систем",
    "settings.integrations.action.connect": "Підключити",
    "settings.integrations.action.manage": "Керувати",
    "settings.integrations.action.reconnect": "Перепідключити",
    "settings.integrations.action.disconnect": "Відключити",
    "settings.integrations.action.cancel": "Скасувати",
    "settings.integrations.tooltip.connect": "Підключити інтеграцію",
    "settings.integrations.tooltip.connectSoon":
      "Потік підключення скоро буде доступний",
    "settings.integrations.tooltip.manage": "Керувати інтеграцією",
    "settings.integrations.tooltip.reconnect": "Перепідключити Google-акаунт",
    "settings.integrations.tooltip.disconnect": "Відключити інтеграцію",
    "settings.integrations.tooltip.soon": "Ця інтеграція скоро буде доступна",
    "settings.integrations.status.connected": "Підключено",
    "settings.integrations.status.actionRequired":
      "Потрібна дія. Оберіть ресурс або перепідключіть це джерело.",
    "settings.integrations.property": "Ресурс: {propertyName}",
    "settings.integrations.gsc.connected":
      "Google Search Console підключено, синхронізацію запущено.",
    "settings.integrations.gsc.disconnected":
      "Google Search Console відключено.",
    "settings.integrations.gsc.connectFailed":
      "Не вдалося підключити Google Search Console.",
    "settings.integrations.gsc.disconnectFailed":
      "Не вдалося відключити Google Search Console.",
    "settings.integrations.gsc.selectFailed":
      "Не вдалося зберегти вибраний сайт Search Console.",
    "settings.integrations.gsc.selectTitle": "Оберіть сайт Search Console",
    "settings.integrations.gsc.selectDescription":
      "Виберіть підтверджений ресурс сайту, який Nobori має синхронізувати для цього бренду.",
    "settings.integrations.gsc.connectSite": "Підключити сайт",
    "settings.integrations.gsc.loadingSites": "Завантажуємо сайти...",
    "settings.integrations.gsc.noSites":
      "Для цього Google-акаунта не знайдено сайтів Search Console.",
    "settings.integrations.gsc.unverifiedSite":
      "Не підтверджено в Search Console",
    "settings.integrations.empty.title": "Інтеграцій не знайдено",
    "settings.integrations.empty.subtitle":
      "Спробуйте інший пошук або фільтр статусу.",
    "date.today": "Сьогодні",
    "date.yesterday": "Вчора",
    "date.last7Days": "Останні 7 днів",
    "date.last14Days": "Останні 14 днів",
    "date.last30Days": "Останні 30 днів",
    "date.custom": "Власний діапазон",
    "dashboard.competitors.loadingTitle":
      "Шукаємо ваших основних конкурентів...",
    "dashboard.competitors.loadingSubtitle":
      "Це може зайняти кілька хвилин залежно від кількості оброблених промптів",
    "dashboard.competitors.bannerTitle": "Додайте більше конкурентів",
    "dashboard.competitors.bannerBody":
      "Ми автоматично додали конкурентів. Перегляньте їх або додайте ще.",
    "dashboard.competitors.bannerButton": "Додати конкурента",
    "legacy.want.guidance.book.a.free.1.1.call.kjim3o":
      "Потрібна допомога? Забронюйте безкоштовний дзвінок 1:1",
    "legacy.well.demo.the.platform.and.show.how.to.dwmz3x":
      "Ми покажемо платформу та як покращити вашу видимість в AI-пошуку",
    "legacy.book.a.call.1lrete7": "Забронювати дзвінок",
    "legacy.visibility.score.1nllkiz": "Оцінка видимості",
    "legacy.percentage.of.prompts.where.your.brand.appears.in.dajnya":
      "Відсоток промптів, у відповідях на які згенерованих AI згадується ваш бренд",
    "legacy.competitors.94sz4m": "Конкуренти",
    "legacy.show.all.33ncpz": "Показати все",
    "legacy.compare.your.brands.visibility.across.chosen.competitors.sv9nis":
      "Порівняйте видимість вашого бренду серед вибраних конкурентів",
    "legacy.cited.domains.1iczww9": "Цитовані домени",
    "legacy.domains.ai.platforms.cite.to.answer.your.prompts.vrg10o":
      "Домени, які AI-платформи цитують у відповідях на ваші промпти",
    "dataChat.hero.title": "Скажіть, над чим попрацювати",
    "dataChat.hero.subtitle":
      "Аналізуйте видимість, конкурентів, промпти й цитовані джерела",
    "dataChat.hero.startWith": "або почніть з одного з варіантів",
    "dataChat.starter.visibilityGaps": "Проаналізувати прогалини видимості",
    "dataChat.starter.citationGaps": "Знайти прогалини цитувань",
    "dataChat.starter.competitors": "Проаналізувати конкурентів",
    "dataChat.starter.recentChanges": "Підсумувати останні зміни",
    "dataChat.placeholder.ask":
      "Запитайте про прогалини видимості, конкурентів, промпти або цитовані домени...",
    "dataChat.placeholder.summarize":
      "Підсумуй зміни видимості {brandName} за останні {days} днів...",
    "dataChat.placeholder.citationGaps":
      "Знайди прогалини цитувань, де перемагають конкуренти...",
    "dataChat.placeholder.compareCompetitors":
      "Порівняй мій бренд із найсильнішим конкурентом...",
    "dataChat.loading.gathering": "Збираємо дані Nobori",
    "dataChat.loading.savingQuestion": "Зберігаємо ваше запитання",
    "dataChat.loading.planning": "Плануємо аналіз",
    "dataChat.loading.queryingSources": "Опрацьовуємо {sources}",
    "dataChat.loading.sourcesCompleted":
      "Завершено {completed} з {total} запитів до даних",
    "dataChat.loading.checkingClusters": "Перевіряємо кластери промптів",
    "dataChat.loading.reviewingCitations": "Переглядаємо дані цитувань",
    "dataChat.loading.comparingCompetitors": "Порівнюємо видимість конкурентів",
    "dataChat.loading.checkingTasks": "Перевіряємо задачі й пріоритети",
    "dataChat.loading.reviewingPrompts": "Переглядаємо результати промптів",
    "dataChat.loading.reviewingVisibility": "Переглядаємо дані видимості",
    "dataChat.loading.reviewingRelevant": "Переглядаємо релевантні дані Nobori",
    "dataChat.loading.preparing": "Готуємо відповідь",
    "dataChat.loading.chat": "Завантажуємо чат...",
    "dataChat.action.copyResponse": "Скопіювати відповідь",
    "dataChat.action.copiedResponse": "Відповідь скопійовано",
    "dataChat.action.tryAgain": "Спробувати ще раз",
    "dataChat.action.cancel": "Скасувати",
    "dataChat.action.send": "Надіслати",
    "dataChat.action.share": "Поділитися",
    "dataChat.action.delete": "Видалити",
    "dataChat.action.copyMessage": "Скопіювати повідомлення",
    "dataChat.action.copiedMessage": "Повідомлення скопійовано",
    "dataChat.action.editMessage": "Редагувати повідомлення",
    "dataChat.soon": "Скоро",
    "dataChat.shareSoon": "Можливість поділитися скоро буде доступна",
    "dataChat.attachSoon": "Додавання файлів скоро буде доступне",
    "dataChat.thread.new": "Новий чат",
    "dataChat.shell.learn": "Дізнатися",
    "dataChat.sidebar.browse": "Огляд",
    "dataChat.sidebar.chat": "Чат",
    "dataChat.sidebar.newBadge": "нове",
    "dataChat.sidebar.tasks": "Задачі",
    "dataChat.sidebar.searchChats": "Пошук чатів...",
    "dataChat.sidebar.pinned": "Закріплені",
    "dataChat.sidebar.today": "Сьогодні",
    "dataChat.sidebar.last7Days": "Останні 7 днів",
    "dataChat.sidebar.last30Days": "Останні 30 днів",
    "dataChat.sidebar.older": "Старіші",
    "dataChat.sidebar.loadingChats": "Завантажуємо чати...",
    "dataChat.sidebar.noChats": "Збережених чатів ще немає.",
    "dataChat.sidebar.chatOptions": "Опції чату",
    "dataChat.sidebar.rename": "Перейменувати",
    "dataChat.sidebar.pin": "Закріпити в чаті",
    "dataChat.sidebar.unpin": "Відкріпити чат",
    "dataChat.sidebar.deleteTitle": "Видалити чат?",
    "dataChat.sidebar.deleteDescription": "Буде видалено",
    "dataChat.sidebar.thisChat": "цей чат",
    "dataChat.toast.loadFailed": "Не вдалося завантажити цей чат",
    "dataChat.toast.answerFailed":
      "Не вдалося відповісти на основі ваших даних",
    "dataChat.toast.copyAnswerFailed": "Не вдалося скопіювати відповідь",
    "dataChat.toast.copyMessageFailed": "Не вдалося скопіювати повідомлення",
    "dataChat.toast.reloadBeforeRetry":
      "Перезавантажте цей чат, перш ніж повторити відповідь",
    "dataChat.error.selectBrand": "Спочатку виберіть бренд",
  },
};
